import React, { useState, useEffect } from 'react'
import Grid from '@mui/material/Grid'
import TextField from '@/v2/components/common/TextField'
import Typography from '@/v2/components/common/Typography'
import Divider from '@/v2/components/common/Divider'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Button from '@/v2/components/common/Button'
import { CancelOutlined, EditOutlined, SaveOutlined } from '@mui/icons-material'
import { useUpdateClaimInfo } from '@/v2/hooks/insurance/useClaims'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import PayerSearch from '@/v2/components/common/PayerSearch'

const Payer = ({
  values = {},
  insuranceId,
  claimId,
  patientId,
  viewOnly = false,
}) => {
  const theme = useTheme()
  const { patientInfo } = values
  const [allowPayerEdit, setAllowPayerEdit] = useState(false)
  const [payerData, setPayerData] = useState(patientInfo?.payer)
  const { updateClaim } = useUpdateClaimInfo(insuranceId, claimId, patientId)
  const { onOpenSnackbar } = useSnackbarContext()
  const handleSave = async () => {
    const res = await updateClaim({
      body: {
        insuranceId,
        patientInfoAttributes: {
          insurancePayerId: payerData?.id,
        },
      },
    })
    if (!res.error) {
      setAllowPayerEdit(false)
      onOpenSnackbar('Patient information updated successfully', 'success')
    } else {
      onOpenSnackbar(res.error, 'error')
    }
  }

  const handleCancel = () => {
    setAllowPayerEdit(false)
    setPayerData(patientInfo?.payer)
  }

  useEffect(() => {
    setPayerData(patientInfo?.payer)
  }, [patientInfo])

  return (
    <>
      <Grid item xs={12} sx={{ marginTop: '12px' }}>
        <Typography variant="h5" bold fontSize="16px">
          Payer Information
        </Typography>
        <Divider margin="5px 0px" />
      </Grid>

      <>
        {patientInfo && (
          <>
            <Grid item xs={8}>
              <PayerSearch
                fullWidth
                name="insurance"
                label="Insurance"
                value={
                  payerData ? `${payerData?.code} - ${payerData?.name}` : ''
                }
                disabled={!allowPayerEdit}
                outlineColor={
                  allowPayerEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                onChange={value => {
                  if (!value) {
                    setPayerData(null)
                    return
                  }
                  setPayerData({ ...payerData, ...value })
                }}
              />
            </Grid>
            {!viewOnly && (
              <Grid item xs={2} sx={{ marginTop: '30px' }}>
                <Box display="flex" flexDirection="row">
                  <Button
                    variant="text"
                    disableElevation
                    aria-label={allowPayerEdit ? 'Cancel' : 'Edit Payer'}
                    startIcon={
                      allowPayerEdit ? (
                        <CancelOutlined color="error" />
                      ) : (
                        <EditOutlined color="link" />
                      )
                    }
                    onClick={() => {
                      if (allowPayerEdit) {
                        handleCancel()
                      }
                      setAllowPayerEdit(!allowPayerEdit)
                    }}
                    sx={{
                      color: '#000',
                      marginBottom: '4px',
                      '&:hover': {
                        background: theme.palette.silver.dark,
                      },
                    }}
                  >
                    {allowPayerEdit ? 'Cancel' : 'Edit'}
                  </Button>
                  {allowPayerEdit && (
                    <Button
                      variant="text"
                      disableElevation
                      startIcon={<SaveOutlined color="link" />}
                      onClick={handleSave}
                      sx={{
                        color: '#000',
                        marginBottom: '4px',
                        '&:hover': {
                          background: theme.palette.silver.dark,
                        },
                        marginLeft: '10px',
                      }}
                    >
                      Save
                    </Button>
                  )}
                </Box>
              </Grid>
            )}
          </>
        )}
      </>
    </>
  )
}

export default Payer
